/**
 * App-wide diagnostic logger. When Dev Mode is enabled, captures API calls,
 * errors, console output, and user actions into a circular buffer that can
 * be exported as a JSON file for offline review.
 *
 * Usage:
 *   import { debugService } from '../services/debugService';
 *   debugService.logAction('template_save', { id: 42, name: 'Welcome' });
 */

// Static top-level import for the API base URL. The prior version of
// fetchDeployVersion used `require('../config/env')` inside an async
// function body, expecting webpack to bundle it dynamically; in practice
// that path evidently failed under CRA's webpack — the dev-mode export
// 2026-05-23T15-50-15Z showed meta.deploy_sha still null. A static import
// is the canonical pattern (matches the rest of the codebase) and is
// guaranteed to resolve at build time. config/env has no debugService
// dependency, so this introduces no cycle.
import { REACT_APP_API_URL } from '../config/env';
// Pure coordinate helper for pin-markup drawing (imports nothing back from
// this module, so no cycle).
import { annotationScale, CAPTURE_PRIVATE_ATTR, isInCapturePrivateRegion } from '../components/debug/debugInspectUtils';

// ── Event types ──────────────────────────────────────────────────────

export const EVENT_TYPES = {
  API_REQUEST: 'api_request',
  API_RESPONSE: 'api_response',
  API_ERROR: 'api_error',
  CONSOLE_ERROR: 'console_error',
  CONSOLE_WARN: 'console_warn',
  UNHANDLED_ERROR: 'unhandled_error',
  UNHANDLED_REJECTION: 'unhandled_rejection',
  REACT_ERROR: 'react_error',
  ACTION: 'action',
  ROUTE_CHANGE: 'route_change',
  AI_WORKFLOW: 'ai_workflow',
  MOBILE_NAV: 'mobile_nav',
  MOBILE_LIFECYCLE: 'mobile_lifecycle',
  MOBILE_GESTURE: 'mobile_gesture',
  MOBILE_DATA: 'mobile_data',
  MOBILE_INTERACTION: 'mobile_interaction',
  MOBILE_RENDER: 'mobile_render',
  MOBILE_PERF: 'mobile_perf',
  // c3 — Wave D1 dev-mode upgrades. Surfaces specific to the canvas
  // execution engine flow; previously had to be inferred from the
  // generic ACTION event by string-matching `action` names.
  ENGINE_RUN: 'engine_run',
  CANVAS_RUN_CLICKED: 'canvas_run_clicked',
  ACTION_DISPATCHED: 'action_dispatched',
  CANVAS_STATE_HASH: 'canvas_state_hash',
  // c3 (v2 — infra/canvas-stability-devmode-v2) — render-layer capture.
  // Data layer + API layer were already captured. Render layer was blind,
  // which made canvas visual bugs (e.g. nodes-disappear-on-rename)
  // undiagnosable. These four surface the divergence between what state
  // says and what the DOM actually shows.
  REACT_RENDER: 'react_render',
  VIEWPORT_CHANGE: 'viewport_change',
  DOM_NODE_COUNT: 'dom_node_count',
  COMPUTED_STYLE_SNAPSHOT: 'computed_style_snapshot',
  // c3 (v3 — infra/canvas-render-storm-fix) — bridge the gap between
  // "what React state says" and "what's in the DOM" with a third probe:
  // "what React Flow's internal store says". The dump
  // bsg-debug-2026-05-23T16-35-46Z showed DOM=0 edges while React state
  // had 2. We need to know whether React Flow's internal getEdges() had
  // 0 or 2 — that tells us whether the loss happened at the props->store
  // boundary or at the store->DOM boundary.
  REACT_FLOW_INTERNAL_STATE: 'react_flow_internal_state',
  // c4 (v4 — infra/dev-mode-v4-comprehensive) — six new captures so the
  // canvas can be debugged with a fine-toothed comb without guesswork:
  USER_INTERACTION: 'user_interaction',   // click/keydown/mousedown w/ target
  DOM_MUTATION: 'dom_mutation',           // MutationObserver hits on .react-flow
  PERF_MEASURE: 'perf_measure',           // performance.measure() spans
  MANUAL_STATE_SNAPSHOT: 'manual_state_snapshot', // on-demand full snapshot
  // Smoke Tests (THE SMOKE GATE) — html2canvas page snapshot so a run can
  // evidence a visual item. The first dev-mode capability gap a real smoke
  // test demanded; adding it here is the worked example of upgrading dev mode
  // to support a smoke item (smoke-test-authoring-standard §4).
  SCREENSHOT: 'screenshot',
} as const;

export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

export type EventLevel = 'info' | 'warn' | 'error';

// Wave M (M1): 'shell-v2' + v2 tab modules — the rebuilt mobile sales app's
// dev-mode capture stream (THE SMOKE GATE evidence source for smoke-9-mobile).
export type MobileModule = 'shell' | 'inbox' | 'action_items' | 'meetings' | 'sales' | 'time_tracking' | 'dialer' | 'shared'
  | 'shell-v2' | 'deals-v2' | 'call-v2' | 'text-v2' | 'inbox-v2' | 'todos-v2' | 'proposal-v2'
  // MOBILE-2E P1: the second edition's two menus.
  | 'sales-menu' | 'ops-menu'
  // MOBILE-2E P7: the Crusher queue.
  | 'crusher-v2'
  // MOBILE-2E P6: Upcoming calls.
  | 'upcoming-v2';

export interface DebugEvent {
  id: number;
  ts: string;
  type: EventType;
  level: EventLevel;
  data: Record<string, any>;
}

export interface DebugExport {
  exportedAt: string;
  userAgent: string;
  url: string;
  screenSize: string;
  isMobile: boolean;
  isStandalone: boolean;
  sessionStartedAt: string;
  sessionDuration: string;
  eventCount: number;
  mobileEventCount: number;
  aiWorkflowEventCount: number;
  errorCount: number;
  routeHistory: Array<{ path: string; timestamp: string }>;
  apiSummary: { total: number; errors: number; slowest: Array<{ url: string; durationMs: number }> };
  errorSummary: Array<{ message: string; count: number; lastSeen: string }>;
  events: DebugEvent[];
  // c3 — quick header for the reader. deploy_sha lets the reader confirm
  // which deploy produced the export (matches /api/version's commit_sha).
  // last_action_before_export shows the final user-triggered action so
  // the reader knows what the user was doing when they hit "download".
  meta: {
    deploy_sha: string | null;
    deploy_sha_short: string | null;
    deploy_environment: string | null;
    last_action_before_export: { ts: string; action: string; data: Record<string, any> } | null;
  };
}

// ── Helpers ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'bsg_dev_mode';
// c2: persisted /api/version cache so deploy_sha is available in the first
// export after a fresh page load, before fetchDeployVersion finishes.
const DEPLOY_VERSION_KEY = 'bsg_dev_mode_deploy_version';
const MAX_EVENTS = 5000;
const MAX_BODY_LENGTH = 8000;

// ── What dev mode may keep (SEC-W4-166) ──────────────────────────────────
// Dev mode used to record every request and response body word for word. A
// smoke run switches dev mode on by itself and posts what it recorded to the
// server, where every manage_settings holder can open it, and a Debug Session
// attaches the same bodies to a finding (and to the ZIP handed to a Claude
// chat). So a client's key typed into the credential vault, a password, or a
// connection token the server handed back sat in that evidence in clear.
// Scrubbed HERE, where the copy is made: the server redacts again when it
// stores and serves (backend utils/captureRedaction.ts), but the browser is
// the root — nothing it never kept can leak from anywhere downstream.

/** A key whose value is a credential. A key ENDING in "token" is one
 *  (`token`, `access_token`, `csrfToken`); `input_tokens` and design
 *  `tokenMismatches` are not. */
const CAPTURE_SECRET_KEY_RE = /(authorization|cookie|token$|secret|passw(or)?d|api[-_]?key|access[-_]?key|private[-_]?key|client[-_]?secret|card[-_]?number|_encrypted$)/i;
/** The short credential words count only as a WHOLE word of the key
 *  (`ssn`, `user_ssn`, `userSSN`, `newPwd`, `cvv2`): matched anywhere, "ssn"
 *  took `businessName`, `processName` and `className` with it (SEC-W4-166
 *  review). */
const SHORT_SECRET_WORD_RE = /^(?:ssn|pwd|cvv2?|cvc2?)$/;

/** Is this key's value a credential? */
export function isCaptureSecretKey(key: string): boolean {
  if (CAPTURE_SECRET_KEY_RE.test(key)) return true;
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((w) => SHORT_SECRET_WORD_RE.test(w));
}

/** Routes whose bodies are credentials end to end: nothing of either body is
 *  kept (the request line, status and timing still are). */
const CREDENTIAL_ROUTE_RE = /^(?:https?:\/\/[^/]+)?(?:\/api)?\/(?:client-integrations(?:[/?]|$)|auth\/(?:login|accept|forgot|reset|join|invite|session)(?:[/?]|$)|portal\/auth(?:[/?]|$)|oauth(?:[/?]|$)|mcp\/oauth(?:[/?]|$)|mcp-connections(?:\/rotate)?(?:\?|$)|settings\/password(?:[/?]|$))/i;

export const CAPTURE_REDACTED = '[REDACTED]';
export const NOT_RECORDED_CREDENTIAL = '[not recorded: credential route]';

/** Is this a route whose bodies dev mode never keeps? */
export function isCredentialRoute(url: string): boolean {
  return CREDENTIAL_ROUTE_RE.test(String(url || ''));
}

const TEXT_SCRUB_RULES: Array<[RegExp, string | ((...m: string[]) => string)]> = [
  // "secret":"…" inside JSON text that was cut short and no longer parses.
  [/"([^"\\]{1,80})"(\s*:\s*)"((?:[^"\\]|\\.)*)"/g,
    (whole: string, key: string, sep: string) => (isCaptureSecretKey(key) ? `"${key}"${sep}"${CAPTURE_REDACTED}"` : whole)],
  [/([?&](?:token|access_token|key|api_key|secret|signature|sig|code|password)=)[^&#\s"']+/gi, `$1${CAPTURE_REDACTED}`],
  [/\b((?:bearer|basic)\s+)[A-Za-z0-9._~+/-]{8,}=*/gi, `$1${CAPTURE_REDACTED}`],
  // A JWT (e.g. the voice token). The second alternative eats the rest of a
  // run that starts like a token but is not one: alone, `\beyJ…` restarted
  // after every dash of a run like "eyJ-eyJ-…" and read to its end each time
  // (quadratic on text anyone can type into a record the recorder then sees).
  // A later start in the same run can only succeed if the first did, so this
  // finds exactly the same tokens; only a match with a dot is one.
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}|\beyJ[A-Za-z0-9_-]*/g,
    (m: string) => (m.includes('.') ? CAPTURE_REDACTED : m)],
  [/\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{8,}/g, CAPTURE_REDACTED],
  [/\bya29\.[A-Za-z0-9._-]+/g, CAPTURE_REDACTED],
  [/\btpl(?:a|r|ac|v|va|vr|vc)_[A-Za-z0-9_-]{8,}/g, CAPTURE_REDACTED],
  // A client-portal invitation (`cpi_`) is a claim to a client's login, and the
  // assessment prep's start link (`cps_`, Phase 2c) mails one — never in an export.
  [/\bcp[is]_[a-f0-9]{40}\b/g, CAPTURE_REDACTED],
];

// The scrub WALKS a value where JSON.stringify used to serialize it, so it
// must be safe on anything the app hands the console or the network layer:
// a cycle is cut where it closes, a wide or heavily shared graph (a React
// event's fiber tree) is cut at a budget instead of being walked down every
// path to each node, and nothing it reads may throw out of the patched
// console or the request interceptor. Before these guards an object with
// three self-references ran the tab out of memory.
export const CAPTURE_CIRCULAR = '[circular]';
export const CAPTURE_TOO_LARGE = '[too large to record]';
export const CAPTURE_UNSERIALIZABLE = '[circular or unserializable]';
const SCRUB_OBJECT_BUDGET = 5000;

/** One walk's bookkeeping: the objects on the current path, and how many more
 *  objects the walk may visit. */
interface ScrubWalk { path: Set<object>; budget: number }
const newWalk = (): ScrubWalk => ({ path: new Set(), budget: SCRUB_OBJECT_BUDGET });

function scrubText(s: string, depth: number, walk: ScrubWalk = newWalk()): string {
  if (!s || /^data:[a-z]+\/[a-z0-9.+-]+;base64,/i.test(s)) return s;
  const t = s.trimStart();
  if ((t.startsWith('{') || t.startsWith('[')) && depth < 24) {
    try {
      const parsed = JSON.parse(s);
      if (parsed && typeof parsed === 'object') return JSON.stringify(scrubValue(parsed, depth + 1, walk));
    } catch { /* not JSON — the text rules below */ }
  }
  let out = s;
  for (const [re, rep] of TEXT_SCRUB_RULES) out = out.replace(re, rep as any);
  return out;
}

/** Credentials out of a captured value, deeply. A value with its own JSON
 *  form (an axios error) is scrubbed in that form; a file, form or DOM node is
 *  named, not walked (JSON.stringify would have recorded nothing of it).
 *  Never throws. */
export function scrubCaptureValue(value: any): any {
  try {
    return scrubValue(value, 0, newWalk());
  } catch {
    return CAPTURE_UNSERIALIZABLE;
  }
}
function isOpaque(v: object): boolean {
  return (typeof Blob !== 'undefined' && v instanceof Blob)
    || (typeof FormData !== 'undefined' && v instanceof FormData)
    || (typeof Node !== 'undefined' && v instanceof Node)
    || (typeof ArrayBuffer !== 'undefined' && (v instanceof ArrayBuffer || ArrayBuffer.isView(v)));
}
function scrubValue(v: any, depth: number, walk: ScrubWalk): any {
  if (v === undefined || v === null) return v;
  if (typeof v === 'string') return scrubText(v, depth, walk);
  if (typeof v !== 'object') return v;
  if (depth > 24) return '[too deep to record]';
  if (walk.path.has(v)) return CAPTURE_CIRCULAR;
  if (--walk.budget < 0) return CAPTURE_TOO_LARGE;
  walk.path.add(v);
  try {
    return scrubObject(v, depth, walk);
  } finally {
    walk.path.delete(v);
  }
}
function scrubObject(v: object, depth: number, walk: ScrubWalk): any {
  if (Array.isArray(v)) return v.map((x) => scrubValue(x, depth + 1, walk));
  let toJSON: unknown;
  try { toJSON = (v as any).toJSON; } catch { toJSON = undefined; }
  if (typeof toJSON === 'function') {
    try { return scrubValue(toJSON.call(v), depth + 1, walk); } catch { /* walk it instead */ }
  }
  if (typeof URLSearchParams !== 'undefined' && v instanceof URLSearchParams) return scrubText(`?${v.toString()}`, depth, walk).slice(1);
  if (isOpaque(v)) return `[${v.constructor?.name || 'object'}]`;
  const out: Record<string, any> = {};
  if (v instanceof Error) { out.name = v.name; out.message = scrubText(String(v.message), depth, walk); }
  for (const k of Object.keys(v)) {
    // A credential-named value is replaced without being read.
    if (isCaptureSecretKey(k)) { out[k] = CAPTURE_REDACTED; continue; }
    let x: any;
    try { x = (v as any)[k]; } catch { out[k] = '[unreadable]'; continue; }
    out[k] = scrubValue(x, depth + 1, walk);
  }
  return out;
}

/** One console argument as dev mode records it. Never throws. */
function scrubConsoleArg(a: any): string {
  try {
    return typeof a === 'object' ? safeStringify(scrubCaptureValue(a)) : scrubText(String(a), 0);
  } catch {
    return CAPTURE_UNSERIALIZABLE;
  }
}

/** What dev mode keeps of a request or response body: nothing on a
 *  credential route, otherwise the body with its credentials replaced, cut to
 *  the length cap. */
export function scrubCaptureBody(body: any, url: string): any {
  if (body === undefined || body === null) return body;
  if (isCredentialRoute(url)) return NOT_RECORDED_CREDENTIAL;
  return truncateBody(scrubCaptureValue(body));
}

// ── Private answers and private regions (SEC-W4-054) ─────────────────────
// What dev mode records is readable by every manage_settings holder — a wider
// audience than a restricted agent's record or the money screens are built
// for. The server marks those answers with X-Capture-Private (backend
// middleware/privateCapture.ts); dev mode keeps neither the body of a marked
// answer nor the body of the request that asked for it. On screen, a region
// carrying the attribute below is left out of every screenshot dev mode takes,
// out of an inspected element's HTML, and out of a click's recorded text.

export const PRIVATE_CAPTURE_HEADER = 'x-capture-private';
export const NOT_RECORDED_PRIVATE = '[not recorded: private answer]';
/** Put this attribute on a region whose content the testing recorder must not
 *  keep: `<Box {...CAPTURE_PRIVATE_PROPS}>`. */
export { CAPTURE_PRIVATE_ATTR };
export const CAPTURE_PRIVATE_PROPS = { 'data-capture-private': '' } as const;

/** Did the server mark this answer private? Takes axios's headers (an
 *  AxiosHeaders, or a plain object in tests). */
export function isPrivateCaptureResponse(headers: any): boolean {
  if (!headers) return false;
  try {
    const v = typeof headers.get === 'function'
      ? headers.get(PRIVATE_CAPTURE_HEADER)
      : (headers[PRIVATE_CAPTURE_HEADER] ?? headers['X-Capture-Private']);
    return v != null && String(v) !== '' && String(v) !== '0';
  } catch {
    return false;
  }
}

/** Is this element inside a private region? */
export const isCapturePrivateElement = isInCapturePrivateRegion;

/** html2canvas's ignoreElements test for the parts of the page dev mode never
 *  draws: its own tools, transient tooltips/toasts, and private regions. */
export function ignoredInCapture(el: Element): boolean {
  const h = el as HTMLElement;
  return h.hasAttribute?.('data-smoke-hud') === true
    || h.hasAttribute?.('data-debug-overlay') === true
    || h.hasAttribute?.('data-debug-inspector') === true
    || h.hasAttribute?.(CAPTURE_PRIVATE_ATTR) === true
    // Tooltips/toasts portal into <body> with no data-* marker — a
    // hovered tooltip baked itself into a prod export screenshot.
    || h.matches?.('.MuiTooltip-popper, [role="tooltip"], .MuiSnackbar-root, .hub-toast-v2') === true;
}

/** The smoke run's quick screenshot leaves out its own HUD, transient
 *  tooltips/toasts and private regions. */
export function ignoredInSmokeShot(el: Element): boolean {
  const h = el as HTMLElement;
  return h.hasAttribute?.('data-smoke-hud') === true
    || h.hasAttribute?.(CAPTURE_PRIVATE_ATTR) === true
    || h.matches?.('.MuiTooltip-popper, [role="tooltip"], .MuiSnackbar-root, .hub-toast-v2') === true;
}

function truncateBody(body: any): any {
  if (body === undefined || body === null) return body;
  try {
    const s = typeof body === 'string' ? body : JSON.stringify(body);
    if (s.length <= MAX_BODY_LENGTH) return body;
    return typeof body === 'string'
      ? s.slice(0, MAX_BODY_LENGTH) + '... [truncated]'
      : JSON.parse(s.slice(0, MAX_BODY_LENGTH) + '"}');
  } catch {
    return '[unserializable]';
  }
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

// ── Service ──────────────────────────────────────────────────────────

type Listener = () => void;

class DebugService {
  private events: DebugEvent[] = [];
  private seq = 0;
  private enabled: boolean;
  private sessionStart: number;
  private listeners: Set<Listener> = new Set();
  private origConsoleError: typeof console.error;
  private origConsoleWarn: typeof console.warn;
  private patchedConsole = false;
  private routeHistory: Array<{ path: string; timestamp: string }> = [];
  // Smoke Tests: while a run is active, every pushed event is tagged with the
  // run token + current item id so a run's capture can be filtered out of the
  // shared buffer (exportSmokeSession).
  private smokeSession: { runToken: string; itemId: string | null } | null = null;
  // Remember whether dev mode was already on when a smoke run force-enabled it,
  // so stopSmokeSession can restore the prior state (don't leave capture ON for
  // a user who started with it off).
  private smokePrevEnabled = false;

  // c3 — deploy_sha is cached after the first fetch and surfaced in exportSession
  // so the reader can verify which Railway deploy produced the export. Populated
  // by fetchDeployVersion() (called by DevModeIndicator on toggle-on).
  private deployVersion: { commit_sha: string; commit_sha_short: string; environment: string } | null = null;

  // Reused screen-capture stream for true-pixel screenshots (Debug Session
  // Builder). Prompted once per session, stopped by stopDisplayStream().
  private displayStream: MediaStream | null = null;

  constructor() {
    this.enabled = localStorage.getItem(STORAGE_KEY) === 'true';
    this.sessionStart = Date.now();
    this.origConsoleError = console.error;
    this.origConsoleWarn = console.warn;
    // c2: hydrate deployVersion from localStorage so the first export after a
    // page load has a non-null deploy_sha, even before fetchDeployVersion's
    // first round-trip completes. The cache is refreshed on toggle/mount.
    try {
      const cached = localStorage.getItem(DEPLOY_VERSION_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.commit_sha) {
          this.deployVersion = {
            commit_sha: String(parsed.commit_sha),
            commit_sha_short: String(parsed.commit_sha_short ?? parsed.commit_sha.slice(0, 7)),
            environment: String(parsed.environment ?? 'unknown'),
          };
        }
      }
    } catch {
      // localStorage parse errors are non-fatal — fetch will refresh.
    }
    if (this.enabled) this.patchConsole();
    this.listenForCSPViolations();
  }

  // ── Public API ───────────────────────────────────────────────────

  get isEnabled(): boolean {
    return this.enabled;
  }

  get sessionStartTime(): number {
    return this.sessionStart;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    localStorage.setItem(STORAGE_KEY, String(this.enabled));
    if (this.enabled) {
      this.sessionStart = Date.now();
      this.routeHistory = [];
      this.patchConsole();
    } else {
      this.restoreConsole();
    }
    this.notify();
    return this.enabled;
  }

  enable(): void {
    if (!this.enabled) this.toggle();
  }

  disable(): void {
    if (this.enabled) this.toggle();
  }

  // ── Smoke Tests: tagged capture sessions ─────────────────────────────
  // A smoke run brackets its capture with start/stop; every event in between
  // is tagged with the run token + current item id (see push). exportSmokeSession
  // slices those events back out for the run's evidence bundle.

  startSmokeSession(runToken: string): void {
    this.smokePrevEnabled = this.enabled; // remember to restore on stop
    this.smokeSession = { runToken, itemId: null };
    this.enable(); // capture must be on for the run
    this.push(EVENT_TYPES.ACTION, 'info', { action: 'smoke_session_start', runToken });
  }

  setSmokeItem(itemId: string | null): void {
    if (this.smokeSession) this.smokeSession.itemId = itemId;
  }

  stopSmokeSession(): void {
    if (this.smokeSession) {
      this.push(EVENT_TYPES.ACTION, 'info', { action: 'smoke_session_stop', runToken: this.smokeSession.runToken });
    }
    this.smokeSession = null;
    // Restore the pre-run capture state — don't leave dev mode ON for a user
    // who started with it off.
    if (!this.smokePrevEnabled) this.disable();
  }

  get activeSmokeRunToken(): string | null {
    return this.smokeSession?.runToken ?? null;
  }

  /** Slice the buffer to one run (optionally one item) into a lean capture the
   *  backend bundles into the run export (shape: events + errorCount + apiSummary). */
  exportSmokeSession(runToken: string, itemId?: string | null): {
    runToken: string; itemId: string | null; exportedAt: string; url: string;
    eventCount: number; errorCount: number; apiSummary: { total: number; errors: number };
    events: DebugEvent[]; meta: { deploy_sha_short: string | null };
  } {
    const filtered = this.events.filter((e) =>
      e.data?.__smoke?.runToken === runToken && (itemId == null || e.data?.__smoke?.itemId === itemId));
    // Count one per call (api_request) — not request+response — so total isn't doubled.
    const apiCalls = filtered.filter((e) => e.type === 'api_request').length;
    const apiErrors = filtered.filter((e) => e.type === 'api_error' || (e.type === 'api_response' && e.level === 'error')).length;
    return {
      runToken, itemId: itemId ?? null,
      exportedAt: new Date().toISOString(),
      url: window.location.href,
      eventCount: filtered.length,
      errorCount: filtered.filter((e) => e.level === 'error').length,
      apiSummary: { total: apiCalls, errors: apiErrors },
      events: filtered,
      meta: { deploy_sha_short: this.deployVersion?.commit_sha_short ?? null },
    };
  }

  /** Race a promise against a timeout; resolves null if it doesn't settle in
   *  time. Keeps a hung html2canvas from stalling the whole debug tool. */
  private withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
    return Promise.race([
      p.catch(() => null),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
    ]);
  }

  /** Capture a downscaled JPEG of the page (html2canvas) as a SCREENSHOT event —
   *  the first dev-mode capability THE SMOKE GATE demanded. The smoke HUD marks
   *  itself with [data-smoke-hud] so it's excluded from the shot. */
  async captureScreenshot(label?: string): Promise<string | null> {
    if (!this.enabled) return null;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        logging: false,
        useCORS: true,
        backgroundColor: null,
        ignoreElements: ignoredInSmokeShot,
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      this.push(EVENT_TYPES.SCREENSHOT, 'info', { label: label || '', route: window.location.pathname, dataUrl });
      return dataUrl;
    } catch (err: any) {
      this.push(EVENT_TYPES.ACTION, 'warn', { action: 'screenshot_failed', message: err?.message });
      return null;
    }
  }

  /** The deploy version (Railway commit) the running bundle came from — pins a
   *  debug session's findings to an exact commit. */
  getDeployVersion(): { commit_sha: string; commit_sha_short: string; environment: string } | null {
    return this.deployVersion;
  }

  /** High-quality html2canvas capture returned as a JPEG Blob — the Debug Session
   *  Builder uploads screenshots as multipart, not base64 events. Full device
   *  scale + 0.92 quality by default; pass a target element to capture just that
   *  node. Excludes the smoke HUD and the debug overlay from the shot. */
  async captureCanvasBlob(opts?: { scale?: number; quality?: number; target?: HTMLElement }): Promise<Blob | null> {
    // An element inside a private region is never drawn (SEC-W4-054).
    if (opts?.target && isCapturePrivateElement(opts.target)) return null;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const scale = opts?.scale ?? Math.min(window.devicePixelRatio || 1, 2);
      const node = opts?.target || document.body;
      // html2canvas can HANG (not just throw) on some pages — the prod calendar
      // session stalled Inspect forever because a target render never resolved.
      // Race it against a timeout so a capture can never block the tool; a null
      // result lets the caller fall back (element-only inspect, or a pasted/True shot).
      const canvas = await this.withTimeout(html2canvas(node, {
        scale,
        logging: false,
        useCORS: true,
        backgroundColor: null,
        ignoreElements: ignoredInCapture,
      }), 9000);
      if (!canvas) return null;
      // A long page at 2x can produce a 25MB+ JPEG (a prod finding shipped one
      // that dwarfed the whole rest of the export). Cap the output to a sane
      // pixel budget so no single shot bloats the ZIP.
      const out = this.capCanvasSize(canvas);
      return await new Promise<Blob | null>((resolve) => out.toBlob((b) => resolve(b), 'image/jpeg', opts?.quality ?? 0.9));
    } catch {
      return null;
    }
  }

  /** Downscale a canvas so its total pixels stay under a budget (~5MP ≈ a
   *  well-under-2MB JPEG). Returns the original if it's already small enough. */
  private capCanvasSize(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const MAX_PIXELS = 5_000_000;
    const pixels = canvas.width * canvas.height;
    if (pixels <= MAX_PIXELS || !canvas.width || !canvas.height) return canvas;
    const scale = Math.sqrt(MAX_PIXELS / pixels);
    const w = Math.max(1, Math.round(canvas.width * scale));
    const h = Math.max(1, Math.round(canvas.height * scale));
    const scaled = document.createElement('canvas');
    scaled.width = w; scaled.height = h;
    const ctx = scaled.getContext('2d');
    if (!ctx) return canvas;
    ctx.drawImage(canvas, 0, 0, w, h);
    return scaled;
  }

  /** Temporarily hide any open MUI tooltip/menu poppers while a screen-pixel
   *  grab runs — getDisplayMedia captures the LITERAL screen, so an open
   *  tooltip (e.g. the True button's own) baked into a prod True shot. Returns
   *  a restore fn. */
  private hideTransientPoppers(): () => void {
    // Private regions too (SEC-W4-054): a screen grab draws the literal
    // screen, so they are hidden for the instant of the grab.
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(`.MuiTooltip-popper, [role="tooltip"], [${CAPTURE_PRIVATE_ATTR}]`));
    const prev = nodes.map((n) => n.style.visibility);
    nodes.forEach((n) => { n.style.visibility = 'hidden'; });
    return () => nodes.forEach((n, i) => { n.style.visibility = prev[i]; });
  }

  /** True-pixel capture via the browser screen-capture API — exactly what's on
   *  screen (incl. canvas/WebGL/video), which html2canvas can only approximate.
   *  Prompts once per session; the stream is reused for later shots and stopped
   *  by stopDisplayStream(). Must be called from a user gesture. */
  async captureTruePixelBlob(): Promise<Blob | null> {
    let restorePoppers: (() => void) | null = null; // D3 — hoisted so the catch can restore
    try {
      if (!this.displayStream || !this.displayStream.active) {
        if (!navigator.mediaDevices || !('getDisplayMedia' in navigator.mediaDevices)) return null;
        this.displayStream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 2 }, audio: false });
        this.displayStream.getVideoTracks()[0]?.addEventListener('ended', () => { this.displayStream = null; });
      }
      const track = this.displayStream.getVideoTracks()[0];
      if (!track) return null;
      restorePoppers = this.hideTransientPoppers(); // no open tooltip in the literal-screen grab
      const video = document.createElement('video');
      video.srcObject = new MediaStream([track]);
      video.muted = true;
      await video.play().catch(() => undefined);
      // Wait longer than one frame interval (getDisplayMedia here runs at
      // frameRate:2 ≈ a frame every 500ms) so a FRESH frame — captured AFTER
      // the tooltip was hidden above — is what we draw, not a stale pre-hide one.
      await new Promise((r) => setTimeout(r, 550));
      const settings = track.getSettings();
      const w = video.videoWidth || settings.width || window.innerWidth;
      const h = video.videoHeight || settings.height || window.innerHeight;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { restorePoppers(); return null; }
      ctx.drawImage(video, 0, 0, w, h);
      video.pause();
      video.srcObject = null;
      restorePoppers();
      return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.95));
    } catch {
      restorePoppers?.();
      return null;
    }
  }
  // (restorePoppers is hoisted above the try so both the ctx-null path and the
  //  catch always restore tooltip visibility — no permanent hide on failure.)

  /** Full-page html2canvas capture with numbered pins drawn on, as a JPEG Blob —
   *  the pin-comment markup uses this so the exported screenshot shows exactly
   *  where the user clicked. Pins are in document (page) coordinates; a pin with
   *  w/h is a REGION and draws as a numbered rectangle instead of a dot. */
  async captureAnnotatedBlob(pins: Array<{ n: number; x: number; y: number; w?: number; h?: number }>, opts?: { scale?: number }): Promise<Blob | null> {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const scale = opts?.scale ?? Math.min(window.devicePixelRatio || 1, 2);
      const canvas = await this.withTimeout(html2canvas(document.body, {
        scale,
        logging: false,
        useCORS: true,
        backgroundColor: null,
        ignoreElements: ignoredInCapture,
      }), 9000);
      if (!canvas) return null;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Reset any residual html2canvas transform and derive the REAL
        // pixels-per-CSS-px factor from the output size — trusting `scale`
        // double-applied the ratio on dpr-2 displays (pins drew at 2x and
        // could land entirely off-canvas).
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const f = annotationScale(canvas.width, document.body.scrollWidth || window.innerWidth);
        const badge = (x: number, y: number, n: number) => {
          const r = 16 * f;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(239,68,68,0.92)';
          ctx.fill();
          ctx.lineWidth = 2 * f;
          ctx.strokeStyle = '#ffffff';
          ctx.stroke();
          ctx.fillStyle = '#ffffff';
          ctx.font = `bold ${18 * f}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(n), x, y);
        };
        for (const p of pins) {
          const x = p.x * f;
          const y = p.y * f;
          if (p.w !== undefined && p.h !== undefined) {
            ctx.fillStyle = 'rgba(239,68,68,0.10)';
            ctx.fillRect(x, y, p.w * f, p.h * f);
            ctx.lineWidth = 3 * f;
            ctx.strokeStyle = 'rgba(239,68,68,0.92)';
            ctx.strokeRect(x, y, p.w * f, p.h * f);
            badge(x, y, p.n); // top-left corner
          } else {
            badge(x, y, p.n);
          }
        }
      }
      return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
    } catch {
      return null;
    }
  }

  /** Stop and release the reused screen-capture stream (call on session end). */
  stopDisplayStream(): void {
    try { this.displayStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    this.displayStream = null;
  }

  /** Whether the true-pixel screen-share stream is live (the overlay shows a
   *  "sharing" indicator + stop button while it is). */
  isDisplayStreamActive(): boolean {
    return !!(this.displayStream && this.displayStream.active);
  }

  /** Debug Session Builder — log a session action (pin placed, element
   *  inspected, evidence staged) as a visible ACTION event, so the Dev Console
   *  shows what the session tooling did. Gated on dev mode like every push. */
  logSessionAction(action: string, data?: Record<string, any>): void {
    this.push(EVENT_TYPES.ACTION, 'info', { action, ...(data || {}) });
  }

  /** Recent network activity as HAR 1.2 entries — attached to a debug-session
   *  finding so a "the button didn't work" capture carries the API calls that
   *  ran around it. (Populated only while capture is enabled; empty otherwise.)
   *  Bodies were scrubbed of credentials and length-capped when recorded
   *  (scrubCaptureBody); the backend redacts again on write. */
  getRecentNetworkHar(sinceMs = 60000): { entries: any[] } {
    const cutoff = Date.now() - sinceMs;
    const recent = this.events.filter((e) => {
      const t = Date.parse(e.ts);
      return Number.isFinite(t) ? t >= cutoff : true;
    });
    const requests = recent.filter((e) => e.type === EVENT_TYPES.API_REQUEST);
    const reqBody = (method: string, url: string, beforeTs: string): any => {
      for (let i = requests.length - 1; i >= 0; i--) {
        const r = requests[i];
        if (r.data.method === method && r.data.url === url && r.ts <= beforeTs) return r.data.body;
      }
      return undefined;
    };
    const asText = (v: any): string => (typeof v === 'string' ? v : JSON.stringify(v ?? null));
    const entries: any[] = [];
    for (const e of recent) {
      if (e.type === EVENT_TYPES.API_RESPONSE) {
        const body = reqBody(e.data.method, e.data.url, e.ts);
        entries.push({
          startedDateTime: e.ts,
          time: e.data.durationMs ?? -1,
          request: { method: e.data.method, url: e.data.url, headers: [], ...(body !== undefined ? { postData: { text: asText(body) } } : {}) },
          response: { status: e.data.status, statusText: '', headers: [], content: { text: asText(e.data.body) } },
        });
      } else if (e.type === EVENT_TYPES.API_ERROR) {
        entries.push({
          startedDateTime: e.ts,
          time: -1,
          request: { method: e.data.method, url: e.data.url, headers: [] },
          response: { status: e.data.status ?? 0, statusText: e.data.message || 'error', headers: [], content: { text: asText(e.data.responseBody) } },
          _error: e.data.message,
        });
      }
    }
    return { entries };
  }

  /** Recent console/error activity around a capture (empty if none). */
  /** Debug Session Builder (F2 — evidence trail): the last ~45s of what the
   *  user DID (clicks/keys/routes) plus what went wrong (console + API
   *  errors), as compact steps. Attached to each finding's repro block so an
   *  export reads as reproduction steps without the owner writing them. */
  getRecentTrail(sinceMs = 45000, cap = 40): Array<{ ts: string; kind: string; summary: string }> {
    const cutoff = Date.now() - sinceMs;
    const out: Array<{ ts: string; kind: string; summary: string }> = [];
    for (const e of this.events) {
      const t = Date.parse(e.ts);
      if (Number.isFinite(t) && t < cutoff) continue;
      let kind: string | null = null;
      let summary = '';
      switch (e.type) {
        case EVENT_TYPES.USER_INTERACTION: {
          const tg = (e.data.target || {}) as Record<string, any>;
          const text = typeof tg.text === 'string' && tg.text ? ` "${tg.text.slice(0, 80)}"` : '';
          kind = String(e.data.kind || 'interaction');
          summary = `${String(tg.tag || '').toLowerCase()}${text}${e.data.key ? ` key=${e.data.key}` : ''}`.trim();
          break;
        }
        case EVENT_TYPES.ROUTE_CHANGE:
          kind = 'route';
          summary = `→ ${e.data.path || ''}${e.data.search || ''}`;
          break;
        case EVENT_TYPES.CONSOLE_ERROR:
        case EVENT_TYPES.CONSOLE_WARN:
          kind = e.type;
          summary = (Array.isArray(e.data.args) ? e.data.args.join(' ') : String(e.data.message || '')).slice(0, 140);
          break;
        case EVENT_TYPES.API_ERROR:
          kind = 'api_error';
          summary = `${e.data.method || ''} ${e.data.url || ''} — ${e.data.message || e.data.status || ''}`.slice(0, 160);
          break;
        case EVENT_TYPES.UNHANDLED_ERROR:
        case EVENT_TYPES.UNHANDLED_REJECTION:
        case EVENT_TYPES.REACT_ERROR:
          kind = 'crash';
          summary = String(e.data.message || '').slice(0, 160);
          break;
        default:
          break;
      }
      if (kind) out.push({ ts: e.ts, kind, summary });
    }
    return out.slice(-cap);
  }

  getRecentConsole(sinceMs = 60000): { errors: any[] } {
    const cutoff = Date.now() - sinceMs;
    const kinds: string[] = [
      EVENT_TYPES.CONSOLE_ERROR, EVENT_TYPES.CONSOLE_WARN, EVENT_TYPES.UNHANDLED_ERROR,
      EVENT_TYPES.UNHANDLED_REJECTION, EVENT_TYPES.REACT_ERROR,
    ];
    const errors = this.events
      .filter((e) => {
        const t = Date.parse(e.ts);
        const recent = Number.isFinite(t) ? t >= cutoff : true;
        return recent && kinds.includes(e.type);
      })
      .map((e) => ({ ts: e.ts, type: e.type, level: e.level, ...e.data }));
    return { errors };
  }

  getEvents(): readonly DebugEvent[] {
    return this.events;
  }

  getErrorCount(): number {
    return this.events.filter(e => e.level === 'error').length;
  }

  getWarnCount(): number {
    return this.events.filter(e => e.level === 'warn').length;
  }

  getRouteHistory(): ReadonlyArray<{ path: string; timestamp: string }> {
    return this.routeHistory;
  }

  clear(): void {
    this.events = [];
    this.seq = 0;
    this.routeHistory = [];
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  // ── Logging methods ──────────────────────────────────────────────

  /** Records a request; returns the event's id so the answer can find it
   *  (a private answer takes the request's body back out). */
  logApiRequest(method: string, url: string, body?: any, meta?: Record<string, any>): number | null {
    return this.push(EVENT_TYPES.API_REQUEST, 'info', {
      method: method.toUpperCase(),
      url,
      body: scrubCaptureBody(body, url),
      // c3 — optional caller-supplied meta. Used by apiClient.ts to attach
      // a snapshot diff summary on PUT /draft requests so the reader can
      // see what actually changed between consecutive autosaves without
      // having to diff the full bodies by hand.
      ...(meta || {}),
    });
  }

  /** Records an answer. `private` (the server's X-Capture-Private marker)
   *  keeps no body, and takes the body back out of the request event
   *  `requestEventId` names. */
  logApiResponse(method: string, url: string, status: number, body: any, durationMs: number,
    opts?: { private?: boolean; requestEventId?: number | null }): void {
    const level: EventLevel = status >= 400 ? 'error' : 'info';
    const priv = !!opts?.private;
    if (priv) this.forgetRequestBody(opts?.requestEventId);
    this.push(EVENT_TYPES.API_RESPONSE, level, {
      method: method.toUpperCase(),
      url,
      status,
      durationMs,
      body: priv ? NOT_RECORDED_PRIVATE : scrubCaptureBody(body, url),
      ...(priv ? { private: true } : {}),
    });
  }

  logApiError(method: string, url: string, error: any, opts?: { requestEventId?: number | null }): void {
    const priv = isPrivateCaptureResponse(error?.response?.headers);
    if (priv) this.forgetRequestBody(opts?.requestEventId);
    this.push(EVENT_TYPES.API_ERROR, 'error', {
      method: method.toUpperCase(),
      url,
      message: error?.message || String(error),
      status: error?.response?.status,
      responseBody: priv ? NOT_RECORDED_PRIVATE : scrubCaptureBody(error?.response?.data, url),
      ...(priv ? { private: true } : {}),
    });
  }

  /** A private answer's request: its body is replaced in the buffer. */
  private forgetRequestBody(eventId: number | null | undefined): void {
    if (eventId == null) return;
    for (let i = this.events.length - 1; i >= 0; i--) {
      const e = this.events[i];
      if (e.id !== eventId) continue;
      if (e.data.body !== undefined) this.events[i] = { ...e, data: { ...e.data, body: NOT_RECORDED_PRIVATE, private: true } };
      return;
    }
  }

  /** True when an error clearly originates from a browser EXTENSION, not our
   *  app (background-redux-new.js, chrome-extension:// frames, injected content
   *  scripts). These are noise in a debug session — the owner saw a Redux-
   *  DevTools/Claude-in-Chrome error and flagged it as an app bug. Drop them. */
  private isExtensionNoise(text: string): boolean {
    return /chrome-extension:\/\/|moz-extension:\/\/|background-redux-new\.js|contentScript\.js|inpage\.js/i.test(text || '');
  }

  logConsoleError(...args: any[]): void {
    // An axios error logged whole serializes its request config — the body it
    // was sending included (a failed vault save would print the key).
    const mapped = args.map(scrubConsoleArg);
    // Only drop when the extension marker is in the PRIMARY message the extension
    // itself printed (arg 0) — NOT anywhere in a serialized nested error's stack,
    // or a real app error whose stack merely routed through an extension frame
    // would be silently lost (the tool's whole job is to capture those).
    if (this.isExtensionNoise(String(args[0] ?? ''))) return;
    this.push(EVENT_TYPES.CONSOLE_ERROR, 'error', { args: mapped });
  }

  logConsoleWarn(...args: any[]): void {
    this.push(EVENT_TYPES.CONSOLE_WARN, 'warn', {
      args: args.map(scrubConsoleArg),
    });
  }

  logUnhandledError(message: string, source?: string, lineno?: number, colno?: number, error?: Error): void {
    // Match the ORIGINATING script (source) — not the stack, which can carry an
    // extension ancestor frame for a genuine app error.
    if (this.isExtensionNoise(`${source || ''} ${message || ''}`)) return; // extension noise
    this.push(EVENT_TYPES.UNHANDLED_ERROR, 'error', {
      message,
      source,
      lineno,
      colno,
      stack: error?.stack,
    });
  }

  logUnhandledRejection(reason: any): void {
    if (this.isExtensionNoise(String(reason?.message || reason || ''))) return; // extension noise (message only, not stack)
    this.push(EVENT_TYPES.UNHANDLED_REJECTION, 'error', {
      message: reason?.message || String(reason),
      stack: reason?.stack,
    });
  }

  logReactError(error: Error, errorInfo: React.ErrorInfo): void {
    this.push(EVENT_TYPES.REACT_ERROR, 'error', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  logAction(action: string, data?: Record<string, any>, level: EventLevel = 'info'): void {
    // c4 v4 #1 — capture stack trace at every action call site. Helps the
    // reader find the exact source line that fired the event, not just
    // its semantic name. Filtered to skip internal debugService frames.
    const stack = this.enabled ? this.captureCallStack(2) : undefined;
    this.push(EVENT_TYPES.ACTION, level, { action, ...data, ...(stack ? { _stack: stack } : {}) });
    if (/^(eng_ai_agent|workflow_ai|workflow_validation|workflow_approval|workflow_runtime_value|workflow_cost|sandbox_test)/.test(action)) {
      this.push(EVENT_TYPES.AI_WORKFLOW, level, { action, ...data });
    }
  }

  /**
   * c4 v4 #1 — capture filtered call stack. Skip internal frames so the
   * reader sees the actual call site (e.g. WorkflowCanvas:794) not the
   * boilerplate inside debugService.
   */
  private captureCallStack(skipExtraFrames = 0): string[] {
    try {
      const stack = new Error().stack;
      if (!stack) return [];
      const lines = stack.split('\n').map(s => s.trim()).filter(Boolean);
      // First line is 'Error' header; next few are this function + caller
      // (logAction) + push() if it's downstream. skipExtraFrames lets the
      // caller skip its own frame too.
      return lines
        .slice(1 + skipExtraFrames)
        .filter(l => !l.includes('debugService.') && !l.includes('debug-service.'))
        .slice(0, 10);
    } catch {
      return [];
    }
  }

  /**
   * c4 v4 #2 — log user interactions (click/keydown/mousedown). Caller
   * (DevModeIndicator's global listener) does the filtering + payload
   * shaping; this is just the buffer push.
   */
  logUserInteraction(data: Record<string, any>): void {
    this.push(EVENT_TYPES.USER_INTERACTION, 'info', data);
  }

  /**
   * c4 v4 #3 — log a DOM mutation observed by MutationObserver.
   */
  logDomMutation(data: Record<string, any>): void {
    this.push(EVENT_TYPES.DOM_MUTATION, 'info', data);
  }

  /**
   * c4 v4 #4 — log a performance.measure() span. Caller computes the
   * duration; this records it.
   */
  logPerfMeasure(data: Record<string, any>): void {
    this.push(EVENT_TYPES.PERF_MEASURE, 'info', data);
  }

  /**
   * c4 v4 #5 — manual state-snapshot registry + trigger. Components
   * (WorkflowCanvas) register a capturer; DevModeIndicator's button
   * calls captureManualSnapshot() which invokes all registered
   * capturers and bundles their results into one MANUAL_STATE_SNAPSHOT
   * event.
   */
  private manualSnapshotCapturers: Map<string, () => Record<string, any>> = new Map();

  registerManualSnapshotCapturer(name: string, capturer: () => Record<string, any>): () => void {
    this.manualSnapshotCapturers.set(name, capturer);
    return () => { this.manualSnapshotCapturers.delete(name); };
  }

  captureManualSnapshot(reason?: string): void {
    if (!this.enabled) return;
    const snapshot: Record<string, any> = {
      reason: reason || 'user_clicked_button',
      captured_at: new Date().toISOString(),
      captures: {} as Record<string, any>,
    };
    for (const [name, capturer] of Array.from(this.manualSnapshotCapturers.entries())) {
      try {
        snapshot.captures[name] = capturer();
      } catch (err: any) {
        snapshot.captures[name] = { error: err?.message || String(err) };
      }
    }
    this.push(EVENT_TYPES.MANUAL_STATE_SNAPSHOT, 'info', snapshot);
  }

  /**
   * c3 — Wave D1 dev-mode logger methods. Distinct event types so post-hoc
   * filtering can isolate engine activity from the broader ACTION stream
   * without string-matching action names.
   */
  logEngineRun(data: Record<string, any>): void {
    this.push(EVENT_TYPES.ENGINE_RUN, 'info', data);
  }

  logCanvasRunClicked(data: Record<string, any>): void {
    this.push(EVENT_TYPES.CANVAS_RUN_CLICKED, 'info', data);
  }

  logActionDispatched(data: Record<string, any>): void {
    this.push(EVENT_TYPES.ACTION_DISPATCHED, 'info', data);
  }

  logCanvasStateHash(data: Record<string, any>): void {
    this.push(EVENT_TYPES.CANVAS_STATE_HASH, 'info', data);
  }

  /**
   * c3 (v2) — render-layer logger methods. All four exist to surface
   * data-vs-render divergence in canvas bugs. Gate callsites on isEnabled
   * before doing expensive DOM queries — push() is a no-op when disabled
   * but the caller still pays for any querySelector/getComputedStyle work.
   */
  logReactRender(data: Record<string, any>): void {
    this.push(EVENT_TYPES.REACT_RENDER, 'info', data);
  }

  logViewportChange(data: Record<string, any>): void {
    this.push(EVENT_TYPES.VIEWPORT_CHANGE, 'info', data);
  }

  logDomNodeCount(data: Record<string, any>): void {
    this.push(EVENT_TYPES.DOM_NODE_COUNT, 'info', data);
  }

  logComputedStyleSnapshot(data: Record<string, any>): void {
    this.push(EVENT_TYPES.COMPUTED_STYLE_SNAPSHOT, 'info', data);
  }

  /**
   * c3 (v3) — Capture React Flow's internal store snapshot. Different from
   * the React state (props passed to <ReactFlow>) and different from the
   * DOM count. Three-way diff lets the reader localize where edges/nodes
   * are being lost: props vs internal store (= React Flow bug) or internal
   * store vs DOM (= reconciliation/CSS bug).
   */
  logReactFlowInternalState(data: Record<string, any>): void {
    this.push(EVENT_TYPES.REACT_FLOW_INTERNAL_STATE, 'info', data);
  }

  /**
   * Fetch /api/version and cache for inclusion in exportSession.
   *
   * Called eagerly by DevModeIndicator on toggle-on and on mount (when dev
   * mode was already enabled). Also persisted to localStorage so subsequent
   * page loads have a deploy_sha available immediately, before the first
   * fetch round-trip completes. The persisted value is used as the initial
   * state in the constructor.
   *
   * c2 fix (infra/canvas-stability-devmode-v2): the prior implementation
   * tried `(process as any).env?.REACT_APP_API_URL` which CRA's
   * DefinePlugin does NOT replace (it only string-substitutes the literal
   * `process.env.REACT_APP_API_URL` pattern). Result: baseUrl was always
   * '/api' regardless of config — which usually works on Railway but
   * obscured the real path. Now imports from the canonical config module.
   * Also: console.warn on failure so the gap is visible in dev-mode
   * exports (and doubles as a self-test that console capture is wired).
   */
  async fetchDeployVersion(): Promise<void> {
    const baseUrl = REACT_APP_API_URL || '/api';
    try {
      // Use bare fetch to avoid the axios interceptor (which would self-log).
      const url = `${baseUrl.replace(/\/$/, '')}/version`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        console.warn(`[debugService] /api/version returned ${res.status}; deploy_sha will be null in exports`);
        return;
      }
      const json = await res.json();
      const data = json?.data ?? json;
      if (data?.commit_sha) {
        this.deployVersion = {
          commit_sha: String(data.commit_sha),
          commit_sha_short: String(data.commit_sha_short ?? data.commit_sha.slice(0, 7)),
          environment: String(data.environment ?? 'unknown'),
        };
        try {
          localStorage.setItem(DEPLOY_VERSION_KEY, JSON.stringify(this.deployVersion));
        } catch {
          // localStorage may be disabled (private mode, quota); not fatal.
        }
      } else {
        console.warn('[debugService] /api/version returned 200 but body had no commit_sha');
      }
    } catch (err: any) {
      console.warn(`[debugService] /api/version fetch failed: ${err?.message || String(err)}; deploy_sha will be null in exports`);
    }
  }

  logAiWorkflow(action: string, data?: Record<string, any>, level: EventLevel = 'info'): void {
    this.push(EVENT_TYPES.AI_WORKFLOW, level, { action, ...data });
  }

  logRouteChange(path: string, search: string): void {
    const entry = { path: path + (search || ''), timestamp: new Date().toISOString() };
    this.routeHistory.push(entry);
    if (this.routeHistory.length > 200) this.routeHistory = this.routeHistory.slice(-200);
    this.push(EVENT_TYPES.ROUTE_CHANGE, 'info', { path, search });
  }

  /** Log TipTap editor state change (node insert, delete, attribute update). */
  logEditorStateChange(action: string, data?: Record<string, any>): void {
    this.push(EVENT_TYPES.ACTION, 'info', { action: `editor_${action}`, ...data });
  }

  /** Log iframe load result (success, error, CSP block). */
  logIframeEvent(action: string, data?: Record<string, any>, level: EventLevel = 'info'): void {
    this.push(EVENT_TYPES.ACTION, level, { action: `iframe_${action}`, ...data });
  }

  /** Log template save with a content hash for verifying round-trip integrity. */
  logTemplateSave(data: Record<string, any>): void {
    this.push(EVENT_TYPES.ACTION, 'info', { action: 'template_save', ...data });
  }

  // ── Mobile-specific logging ─────────────────────────────────────────

  logMobileNav(module: MobileModule, action: string, data?: Record<string, any>): void {
    this.push(EVENT_TYPES.MOBILE_NAV, 'info', { module, action, ...data });
  }

  logMobileLifecycle(module: MobileModule, action: string, data?: Record<string, any>): void {
    this.push(EVENT_TYPES.MOBILE_LIFECYCLE, 'info', { module, action, ...data });
  }

  logMobileGesture(module: MobileModule, gesture: string, data?: Record<string, any>, level: EventLevel = 'info'): void {
    this.push(EVENT_TYPES.MOBILE_GESTURE, level, { module, gesture, ...data });
  }

  logMobileData(module: MobileModule, action: string, data?: Record<string, any>, level: EventLevel = 'info'): void {
    this.push(EVENT_TYPES.MOBILE_DATA, level, { module, action, ...data });
  }

  logMobileInteraction(module: MobileModule, action: string, data?: Record<string, any>): void {
    this.push(EVENT_TYPES.MOBILE_INTERACTION, 'info', { module, action, ...data });
  }

  logMobileRender(module: MobileModule, issue: string, data?: Record<string, any>, level: EventLevel = 'warn'): void {
    this.push(EVENT_TYPES.MOBILE_RENDER, level, { module, issue, ...data });
  }

  logMobilePerf(module: MobileModule, metric: string, durationMs: number, data?: Record<string, any>): void {
    this.push(EVENT_TYPES.MOBILE_PERF, durationMs > 3000 ? 'warn' : 'info', { module, metric, durationMs, ...data });
  }

  getMobileEventCount(): number {
    return this.events.filter(e => e.type.startsWith('mobile_')).length;
  }

  // ── Export ───────────────────────────────────────────────────────

  exportSession(): DebugExport {
    const isMobile = window.innerWidth < 768;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;

    const apiEvents = this.events.filter(e => e.type.startsWith('api_'));
    const aiEvents = this.events.filter(e => e.type === EVENT_TYPES.AI_WORKFLOW);
    const apiErrors = apiEvents.filter(e => e.level === 'error').length;
    const apiResponses = this.events
      .filter(e => e.type === 'api_response' && e.data.durationMs)
      .sort((a, b) => (b.data.durationMs || 0) - (a.data.durationMs || 0))
      .slice(0, 10)
      .map(e => ({ url: `${e.data.method} ${e.data.url}`, durationMs: e.data.durationMs }));

    const errorMap = new Map<string, { count: number; lastSeen: string }>();
    for (const e of this.events.filter(ev => ev.level === 'error')) {
      const msg = e.data.message || e.data.action || e.data.args?.[0] || e.type;
      const key = String(msg).slice(0, 200);
      const existing = errorMap.get(key);
      if (existing) { existing.count++; existing.lastSeen = e.ts; }
      else errorMap.set(key, { count: 1, lastSeen: e.ts });
    }
    const errorSummary = Array.from(errorMap.entries())
      .map(([message, v]) => ({ message, ...v }))
      .sort((a, b) => b.count - a.count);

    // c3 — last_action_before_export scans events backwards for the most
    // recent ACTION event. Surfaced in meta so the reader doesn't have to
    // scroll the full event list to know what the user was doing right
    // before they hit "download".
    let lastAction: { ts: string; action: string; data: Record<string, any> } | null = null;
    for (let i = this.events.length - 1; i >= 0; i--) {
      const e = this.events[i];
      if (e.type === EVENT_TYPES.ACTION) {
        lastAction = { ts: e.ts, action: String(e.data.action || ''), data: { ...e.data } };
        break;
      }
    }

    return {
      exportedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      isMobile,
      isStandalone,
      sessionStartedAt: new Date(this.sessionStart).toISOString(),
      sessionDuration: formatDuration(Date.now() - this.sessionStart),
      eventCount: this.events.length,
      mobileEventCount: this.getMobileEventCount(),
      aiWorkflowEventCount: aiEvents.length,
      errorCount: this.getErrorCount(),
      routeHistory: [...this.routeHistory],
      apiSummary: { total: apiEvents.length, errors: apiErrors, slowest: apiResponses },
      errorSummary,
      events: [...this.events],
      meta: {
        deploy_sha: this.deployVersion?.commit_sha ?? null,
        deploy_sha_short: this.deployVersion?.commit_sha_short ?? null,
        deploy_environment: this.deployVersion?.environment ?? null,
        last_action_before_export: lastAction,
      },
    };
  }

  downloadExport(): void {
    const data = this.exportSession();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bsg-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * c4 v4 #6 — generate a self-contained HTML report alongside the JSON.
   * Visualizes the same event buffer in a scannable format: summary cards,
   * SVG timeline grouped by event type, render-count line chart, action +
   * API + divergence tables. Single HTML file with inline CSS + inline SVG —
   * no external assets, no network calls, viewable offline.
   *
   * The JSON dump is still the source of truth for programmatic analysis;
   * the HTML is for the human reader who wants to see "what happened" at
   * a glance without parsing JSON line-by-line.
   */
  downloadHtmlReport(): void {
    const data = this.exportSession();
    const html = this.buildHtmlReport(data);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bsg-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private buildHtmlReport(data: DebugExport): string {
    const escape = (s: any) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    const events = data.events;
    const sessionStartMs = new Date(data.sessionStartedAt).getTime();
    const sessionEndMs = events.length > 0 ? new Date(events[events.length - 1].ts).getTime() : sessionStartMs + 1000;
    const totalMs = Math.max(1, sessionEndMs - sessionStartMs);

    // Event-type counts.
    const typeCounts = new Map<string, number>();
    for (const e of events) typeCounts.set(e.type, (typeCounts.get(e.type) || 0) + 1);
    const sortedTypes = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]);

    // Timeline rows (one per event type). Each event = a colored dot positioned by ts.
    const typeColor: Record<string, string> = {
      api_request: '#3b82f6', api_response: '#10b981', api_error: '#ef4444',
      action: '#8b5cf6', react_render: '#f59e0b', dom_node_count: '#06b6d4',
      viewport_change: '#a855f7', computed_style_snapshot: '#ec4899',
      canvas_state_hash: '#0ea5e9', react_flow_internal_state: '#14b8a6',
      user_interaction: '#22c55e', dom_mutation: '#f43f5e',
      perf_measure: '#eab308', manual_state_snapshot: '#9333ea',
      console_error: '#dc2626', console_warn: '#d97706',
      route_change: '#6366f1',
    };
    const colorFor = (t: string) => typeColor[t] || '#9ca3af';
    const timelineWidth = 1200;
    const rowHeight = 18;
    const timelineRows = sortedTypes.map(([type], i) => {
      const dots = events
        .filter(e => e.type === type)
        .map(e => {
          const x = ((new Date(e.ts).getTime() - sessionStartMs) / totalMs) * timelineWidth;
          return `<circle cx="${x.toFixed(1)}" cy="${(i * rowHeight + 9).toFixed(1)}" r="2.5" fill="${colorFor(type)}" />`;
        })
        .join('');
      const label = `<text x="-10" y="${i * rowHeight + 12}" text-anchor="end" font-size="10" fill="#374151" font-family="monospace">${escape(type)} (${typeCounts.get(type)})</text>`;
      return label + dots;
    }).join('');
    const timelineHeight = sortedTypes.length * rowHeight;

    // Render-count line chart (WorkflowCanvas + NodeEditorPanel).
    const renderEvents = events.filter(e => e.type === 'react_render');
    const wcRenders = renderEvents.filter(e => e.data?.component === 'WorkflowCanvas');
    const nepRenders = renderEvents.filter(e => e.data?.component === 'NodeEditorPanel');
    const chartWidth = 1200;
    const chartHeight = 120;
    const maxCount = Math.max(1, ...renderEvents.map(e => Number(e.data?.render_count) || 0));
    const renderPath = (points: typeof renderEvents, color: string) => {
      if (points.length === 0) return '';
      const path = points.map((e, i) => {
        const x = ((new Date(e.ts).getTime() - sessionStartMs) / totalMs) * chartWidth;
        const y = chartHeight - (Number(e.data?.render_count) / maxCount) * chartHeight;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      }).join(' ');
      return `<path d="${path}" stroke="${color}" stroke-width="1.5" fill="none" />`;
    };

    // Divergence events (DOM-vs-data mismatch).
    const divergenceEvents = events.filter(e => e.type === 'dom_node_count' && e.data?.divergence === true);

    // Render-storm detection: 8+ renders in any 500ms window.
    const stormWindows: Array<{ start: string; end: string; component: string; count: number }> = [];
    for (const component of ['WorkflowCanvas', 'NodeEditorPanel']) {
      const list = renderEvents.filter(e => e.data?.component === component).map(e => new Date(e.ts).getTime());
      for (let i = 0; i < list.length; i++) {
        const windowEnd = list[i] + 500;
        const inside = list.slice(i).filter(t => t <= windowEnd);
        if (inside.length >= 8) {
          stormWindows.push({
            start: new Date(list[i]).toISOString(),
            end: new Date(inside[inside.length - 1]).toISOString(),
            component,
            count: inside.length,
          });
          // Skip past this storm window
          i += inside.length - 1;
        }
      }
    }

    const actionRows = events.filter(e => e.type === 'action').slice(-50).reverse().map(e => `
      <tr>
        <td>${escape(e.ts.slice(11, 23))}</td>
        <td>${escape(e.data?.action || '?')}</td>
        <td>${escape(e.data?.triggered_by || '')}</td>
        <td>${escape(e.data?.short_circuited ? '⊘ short-circuited' : '')}</td>
      </tr>
    `).join('');

    const apiRows = events.filter(e => e.type === 'api_request').slice(-50).reverse().map(e => {
      const diff = e.data?.snapshot_diff_summary;
      const diffStr = typeof diff === 'object' && diff ? `Δ${diff.total_changes}` : (typeof diff === 'string' ? diff : '');
      return `
      <tr>
        <td>${escape(e.ts.slice(11, 23))}</td>
        <td>${escape(e.data?.method)}</td>
        <td>${escape(e.data?.url)}</td>
        <td>${escape(diffStr)}</td>
      </tr>
    `;
    }).join('');

    const divergenceRows = divergenceEvents.slice(-50).reverse().map(e => `
      <tr>
        <td>${escape(e.ts.slice(11, 23))}</td>
        <td>${escape(e.data?.react_flow_nodes_in_dom)}n / ${escape(e.data?.react_flow_edges_in_dom)}e</td>
        <td>${escape(e.data?.data_node_count)}n / ${escape(e.data?.data_edge_count)}e</td>
        <td>${escape((e.data?.dom_edge_ids || []).join(', '))}</td>
      </tr>
    `).join('');

    const userInteractionRows = events.filter(e => e.type === 'user_interaction').slice(-50).reverse().map(e => `
      <tr>
        <td>${escape(e.ts.slice(11, 23))}</td>
        <td>${escape(e.data?.kind)}</td>
        <td>${escape(e.data?.key || e.data?.button || '')}</td>
        <td>${escape((e.data?.target?.tag || '') + (e.data?.target?.in_node ? ` [node ${e.data.target.in_node}]` : '') + (e.data?.target?.in_edge ? ` [edge ${e.data.target.in_edge}]` : '') + (e.data?.target?.text ? ` "${e.data.target.text}"` : ''))}</td>
      </tr>
    `).join('');

    const errorRows = events.filter(e => e.level === 'error').slice(-20).reverse().map(e => `
      <tr>
        <td>${escape(e.ts.slice(11, 23))}</td>
        <td>${escape(e.type)}</td>
        <td>${escape(JSON.stringify(e.data).slice(0, 200))}</td>
      </tr>
    `).join('');

    return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>BSG Debug Export — ${escape(data.sessionStartedAt)}</title>
<style>
  body { font: 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 16px; background: #f3f4f6; color: #111827; }
  h1 { font-size: 20px; margin: 0 0 12px; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin: 0 0 12px; font-weight: 700; }
  .card { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
  .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
  .stat { background: #f9fafb; border-radius: 6px; padding: 10px 12px; }
  .stat-label { font-size: 10px; text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: 0.5px; }
  .stat-value { font-size: 20px; font-weight: 700; color: #111827; margin-top: 4px; font-variant-numeric: tabular-nums; }
  .meta-line { font-family: "SF Mono", Monaco, monospace; font-size: 12px; color: #374151; margin: 2px 0; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f9fafb; text-align: left; padding: 8px; font-size: 10px; text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: 0.5px; }
  td { padding: 5px 8px; border-bottom: 1px solid #f3f4f6; font-family: "SF Mono", Monaco, monospace; font-size: 11px; vertical-align: top; }
  .alert { background: #fee2e2; border-left: 4px solid #dc2626; padding: 10px 14px; margin-bottom: 12px; border-radius: 4px; font-size: 12px; }
  .alert-strong { font-weight: 700; color: #991b1b; }
  details { font-size: 12px; margin: 6px 0; }
  summary { cursor: pointer; padding: 4px 0; font-weight: 600; color: #4b5563; }
  svg { display: block; max-width: 100%; }
</style>
</head><body>
<h1>🐛 BSG Debug Export</h1>

<div class="card">
  <h2>Session</h2>
  <div class="meta-line"><b>Deploy:</b> ${escape(data.meta?.deploy_sha_short || 'unknown')} (${escape(data.meta?.deploy_environment || '?')})</div>
  <div class="meta-line"><b>URL:</b> ${escape(data.url)}</div>
  <div class="meta-line"><b>Session:</b> ${escape(data.sessionStartedAt)} → ${escape(data.exportedAt)} (${escape(data.sessionDuration)})</div>
  <div class="meta-line"><b>User-agent:</b> ${escape(data.userAgent)}</div>
  ${data.meta?.last_action_before_export ? `<div class="meta-line"><b>Last action before export:</b> ${escape(data.meta.last_action_before_export.action)} at ${escape(data.meta.last_action_before_export.ts.slice(11, 23))}</div>` : ''}
</div>

${stormWindows.length > 0 ? `<div class="alert"><span class="alert-strong">⚠ Render storms detected:</span> ${stormWindows.length} window(s) with 8+ renders in 500ms. <details><summary>Details</summary>${stormWindows.map(s => `<div>${escape(s.start.slice(11, 23))} — ${escape(s.component)} fired ${s.count}× in ${Math.round(new Date(s.end).getTime() - new Date(s.start).getTime())}ms</div>`).join('')}</details></div>` : ''}

${divergenceEvents.length > 0 ? `<div class="alert"><span class="alert-strong">⚠ DOM/data divergence:</span> ${divergenceEvents.length} event(s) where the React Flow DOM count differs from the snapshot count. See divergence table below.</div>` : ''}

<div class="card">
  <h2>Summary</h2>
  <div class="summary-grid">
    <div class="stat"><div class="stat-label">Total Events</div><div class="stat-value">${data.eventCount}</div></div>
    <div class="stat"><div class="stat-label">Errors</div><div class="stat-value">${data.errorCount}</div></div>
    <div class="stat"><div class="stat-label">API Calls</div><div class="stat-value">${data.apiSummary.total}</div></div>
    <div class="stat"><div class="stat-label">API Errors</div><div class="stat-value">${data.apiSummary.errors}</div></div>
    <div class="stat"><div class="stat-label">Render Storms</div><div class="stat-value">${stormWindows.length}</div></div>
    <div class="stat"><div class="stat-label">DOM Divergence</div><div class="stat-value">${divergenceEvents.length}</div></div>
  </div>
</div>

<div class="card">
  <h2>Event Timeline (each dot is one event, x-axis = session time)</h2>
  <svg viewBox="-160 0 ${timelineWidth + 200} ${timelineHeight + 20}" width="100%" height="${timelineHeight + 20}">
    <line x1="0" y1="0" x2="0" y2="${timelineHeight}" stroke="#d1d5db" />
    <line x1="${timelineWidth}" y1="0" x2="${timelineWidth}" y2="${timelineHeight}" stroke="#d1d5db" />
    ${timelineRows}
    <text x="0" y="${timelineHeight + 14}" font-size="9" fill="#9ca3af" font-family="monospace">0s</text>
    <text x="${timelineWidth}" y="${timelineHeight + 14}" font-size="9" fill="#9ca3af" text-anchor="end" font-family="monospace">${(totalMs / 1000).toFixed(1)}s</text>
  </svg>
</div>

<div class="card">
  <h2>Render Count Over Time (WorkflowCanvas = blue, NodeEditorPanel = orange)</h2>
  <svg viewBox="0 0 ${chartWidth} ${chartHeight + 20}" width="100%" height="${chartHeight + 20}">
    <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" fill="#f9fafb" />
    ${renderPath(wcRenders, '#3b82f6')}
    ${renderPath(nepRenders, '#f59e0b')}
    <text x="4" y="12" font-size="10" fill="#9ca3af" font-family="monospace">max ${maxCount}</text>
    <text x="${chartWidth - 4}" y="${chartHeight + 14}" font-size="9" fill="#9ca3af" text-anchor="end" font-family="monospace">${(totalMs / 1000).toFixed(1)}s</text>
  </svg>
</div>

${divergenceEvents.length > 0 ? `
<div class="card">
  <h2>DOM/Data Divergence (last 50)</h2>
  <table>
    <thead><tr><th>Time</th><th>DOM (nodes / edges)</th><th>Data (nodes / edges)</th><th>DOM edge IDs</th></tr></thead>
    <tbody>${divergenceRows}</tbody>
  </table>
</div>
` : ''}

<div class="card">
  <h2>Action Events (last 50)</h2>
  <table>
    <thead><tr><th>Time</th><th>Action</th><th>Triggered By</th><th>Note</th></tr></thead>
    <tbody>${actionRows}</tbody>
  </table>
</div>

<div class="card">
  <h2>API Requests (last 50)</h2>
  <table>
    <thead><tr><th>Time</th><th>Method</th><th>URL</th><th>Diff</th></tr></thead>
    <tbody>${apiRows}</tbody>
  </table>
</div>

${userInteractionRows ? `
<div class="card">
  <h2>User Interactions (last 50)</h2>
  <table>
    <thead><tr><th>Time</th><th>Kind</th><th>Key/Button</th><th>Target</th></tr></thead>
    <tbody>${userInteractionRows}</tbody>
  </table>
</div>
` : ''}

${errorRows ? `
<div class="card">
  <h2>Errors (last 20)</h2>
  <table>
    <thead><tr><th>Time</th><th>Type</th><th>Detail (truncated)</th></tr></thead>
    <tbody>${errorRows}</tbody>
  </table>
</div>
` : ''}

<div class="card">
  <h2>Event-Type Counts</h2>
  <table>
    <thead><tr><th>Type</th><th>Count</th></tr></thead>
    <tbody>${sortedTypes.map(([t, c]) => `<tr><td><span style="display:inline-block;width:10px;height:10px;background:${colorFor(t)};border-radius:50%;margin-right:6px;vertical-align:middle"></span>${escape(t)}</td><td>${c}</td></tr>`).join('')}</tbody>
  </table>
</div>

</body></html>`;
  }

  async copyExportToClipboard(): Promise<boolean> {
    try {
      const data = this.exportSession();
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      return true;
    } catch {
      return false;
    }
  }

  async shareExport(): Promise<boolean> {
    if (!navigator.share) return false;
    try {
      const data = this.exportSession();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const file = new File([blob], `bsg-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`, { type: 'application/json' });
      await navigator.share({ title: 'BSG Debug Export', files: [file] });
      return true;
    } catch {
      return false;
    }
  }

  exportMarkdown(): string {
    const data = this.exportSession();
    const lines: string[] = [];
    lines.push(`# BSG Debug Export`);
    lines.push(`**Exported:** ${data.exportedAt}`);
    lines.push(`**Session:** ${data.sessionStartedAt} (${data.sessionDuration})`);
    lines.push(`**Events:** ${data.eventCount} total, ${data.errorCount} errors`);
    lines.push(`**Screen:** ${data.screenSize} | **URL:** ${data.url}`);
    lines.push('');
    if (data.routeHistory.length > 0) {
      lines.push('## Route History');
      data.routeHistory.forEach(r => lines.push(`- \`${r.path}\` at ${new Date(r.timestamp).toLocaleTimeString()}`));
      lines.push('');
    }
    if (data.apiSummary.slowest.length > 0) {
      lines.push('## Slowest API Calls');
      data.apiSummary.slowest.forEach(s => lines.push(`- ${s.url} — ${s.durationMs}ms`));
      lines.push('');
    }
    if (data.errorSummary.length > 0) {
      lines.push('## Errors');
      data.errorSummary.forEach(e => lines.push(`- **${e.message}** (x${e.count})`));
      lines.push('');
    }
    lines.push('## Event Log');
    lines.push('');
    data.events.forEach(e => {
      const time = new Date(e.ts).toLocaleTimeString('en-US', { hour12: false });
      const lvl = e.level === 'error' ? '**ERROR**' : e.level === 'warn' ? '*WARN*' : '';
      const detail = e.type === 'action' ? e.data.action : `${e.type}`;
      const extra = e.data.url ? ` ${e.data.method || ''} ${e.data.url}` : '';
      lines.push(`- \`${time}\` ${lvl} ${detail}${extra}`.trim());
    });
    return lines.join('\n');
  }

  downloadMarkdownExport(): void {
    const md = this.exportMarkdown();
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bsg-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Internals ────────────────────────────────────────────────────

  private push(type: EventType, level: EventLevel, data: Record<string, any>): number | null {
    if (!this.enabled) return null;
    // Smoke Tests: tag the event with the active run + item so the run's
    // capture can be sliced out of the shared buffer.
    const tagged = this.smokeSession ? { ...data, __smoke: { ...this.smokeSession } } : data;
    const event: DebugEvent = {
      id: ++this.seq,
      ts: new Date().toISOString(),
      type,
      level,
      data: tagged,
    };
    this.events.push(event);
    if (this.events.length > MAX_EVENTS) {
      this.events = this.events.slice(-MAX_EVENTS);
    }
    this.notify();
    return event.id;
  }

  private notify(): void {
    this.listeners.forEach(fn => {
      try { fn(); } catch { /* listener errors shouldn't break the service */ }
    });
  }

  private patchConsole(): void {
    if (this.patchedConsole) return;
    this.patchedConsole = true;
    const self = this;
    console.error = function (...args: any[]) {
      self.logConsoleError(...args);
      self.origConsoleError.apply(console, args);
    };
    console.warn = function (...args: any[]) {
      self.logConsoleWarn(...args);
      self.origConsoleWarn.apply(console, args);
    };
  }

  private restoreConsole(): void {
    if (!this.patchedConsole) return;
    this.patchedConsole = false;
    console.error = this.origConsoleError;
    console.warn = this.origConsoleWarn;
  }

  private listenForCSPViolations(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
      this.push(EVENT_TYPES.ACTION, 'error', {
        action: 'csp_violation',
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        effectiveDirective: e.effectiveDirective,
        originalPolicy: e.originalPolicy?.slice(0, 500),
        sourceFile: e.sourceFile,
        lineNumber: e.lineNumber,
      });
    });
  }
}

function safeStringify(obj: any): string {
  try {
    return JSON.stringify(obj, null, 0).slice(0, 2000);
  } catch {
    return '[circular or unserializable]';
  }
}

export const debugService = new DebugService();
